import fs from 'fs/promises'
import {
  buildClientSchema,
  getIntrospectionQuery,
  GraphQLSchema,
  GraphQLType,
  isEnumType,
  isInputObjectType,
  isInterfaceType,
  isListType,
  isNonNullType,
  isObjectType,
  isScalarType,
  isUnionType
} from 'graphql'
import {SCALARS} from '../builder/schema/scalars.js'

const resolveType = (
  type: GraphQLType
): {tsType: string; isNullable: boolean} => {
  if (isNonNullType(type)) {
    const inner = resolveType(type.ofType)
    return {tsType: inner.tsType, isNullable: false}
  }
  if (isListType(type)) {
    const inner = resolveType(type.ofType)
    return {tsType: `Array<${inner.tsType}>`, isNullable: true}
  }
  if (isScalarType(type)) {
    const knownScalars = Object.keys(SCALARS)
    if (knownScalars.includes(type.name)) {
      return {tsType: type.name, isNullable: true}
    }
    return {tsType: type.name, isNullable: true}
  }
  return {tsType: (type as any).name, isNullable: true}
}

export async function generatePylonTypes(url: string, outputPath: string) {
  const response = await fetch(url, {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({query: getIntrospectionQuery()})
  })

  const {data} = (await response.json()) as any
  const schema: GraphQLSchema = buildClientSchema(data)
  const typeMap = schema.getTypeMap()

  let tsOutput = `// AUTO-GENERATED BY PYLON INTROSPECT\n\n`

  // Append pylon scalars from "@getcronit/pylon"
  tsOutput += `import {ID, Int, Float} from '@getcronit/pylon';\n\n`

  for (const [scalarName, tsType] of Object.entries(SCALARS)) {
    // Skip if TS built.. For example Date or File
    if (
      tsType === 'Date' ||
      tsType === 'File' ||
      scalarName === 'ID' ||
      scalarName === 'Int' ||
      scalarName === 'Float'
    ) {
      continue
    }

    tsOutput += `type ${scalarName} = ${tsType} & {readonly brand?: unique symbol};\n`
  }
  tsOutput += `\n`

  for (const typeName in typeMap) {
    if (typeName.startsWith('__')) continue
    const type = typeMap[typeName]

    if (isObjectType(type)) {
      const isRoot = ['Query', 'Mutation', 'Subscription'].includes(typeName)

      tsOutput += `export interface ${typeName} {\n`
      tsOutput += `  readonly __typename${isRoot ? '?' : ''}: '${typeName}';\n`

      for (const [fieldName, field] of Object.entries(type.getFields())) {
        const {tsType: returnType, isNullable: isReturnNullable} = resolveType(
          field.type
        )

        if (field.args.length > 0) {
          const params = field.args
            .map(arg => {
              const {tsType, isNullable} = resolveType(arg.type)
              return `${arg.name}${isNullable ? '?' : ''}: ${tsType}`
            })
            .join(', ')
          tsOutput += `  ${fieldName}(${params}): ${returnType}${isReturnNullable ? ' | null' : ''};\n`
        } else {
          tsOutput += `  ${fieldName}${isReturnNullable ? '?' : ''}: ${returnType};\n`
        }
      }
      tsOutput += `}\n\n`
    } else if (isInterfaceType(type)) {
      const possibleTypes = schema.getPossibleTypes(type)

      if (possibleTypes.length > 0) {
        // ✅ Treat the Interface as a discriminated union of its implementers
        const union = possibleTypes.map(t => t.name).join(' | ')
        tsOutput += `export type ${typeName} = ${union};\n\n`
      } else {
        // Fallback just in case an interface has 0 implementers defined yet
        tsOutput += `export interface ${typeName} {\n`
        tsOutput += `  readonly __typename: string;\n`
        for (const [fieldName, field] of Object.entries(type.getFields())) {
          const {tsType: returnType, isNullable: isReturnNullable} =
            resolveType(field.type)
          if (field.args.length > 0) {
            const params = field.args
              .map(arg => {
                const {tsType, isNullable} = resolveType(arg.type)
                return `${arg.name}${isNullable ? '?' : ''}: ${tsType}`
              })
              .join(', ')
            tsOutput += `  ${fieldName}(${params}): ${returnType}${isReturnNullable ? ' | null' : ''};\n`
          } else {
            tsOutput += `  ${fieldName}${isReturnNullable ? '?' : ''}: ${returnType};\n`
          }
        }
        tsOutput += `}\n\n`
      }
    } else if (isInputObjectType(type)) {
      tsOutput += `export interface ${typeName} {\n`
      for (const [fieldName, field] of Object.entries(type.getFields())) {
        const {tsType, isNullable} = resolveType(field.type)
        tsOutput += `  ${fieldName}${isNullable ? '?' : ''}: ${tsType};\n`
      }
      tsOutput += `}\n\n`
    } else if (isEnumType(type)) {
      const values = type
        .getValues()
        .map(v => `'${v.name}'`)
        .join(' | ')
      tsOutput += `export type ${typeName} = ${values};\n\n`
    } else if (isUnionType(type)) {
      // For Unions, we just export a union of the specific types (e.g. User | Org)
      // Since the Object types already have strict __typename definitions,
      // TS will automatically understand the union's __typename is 'User' | 'Org'.
      const types = type
        .getTypes()
        .map(t => t.name)
        .join(' | ')
      tsOutput += `export type ${typeName} = ${types};\n\n`
    } else if (isScalarType(type)) {
      const knownScalars = Object.keys(SCALARS)
      if (!knownScalars.includes(typeName)) {
        tsOutput += `export type ${typeName} = any;\n\n`
      }
    }
  }

  const dirPath = outputPath.substring(0, outputPath.lastIndexOf('/'))
  if (dirPath) {
    await fs.mkdir(dirPath, {recursive: true})
  }

  let registry = `export interface RemoteRegistry {\n`
  registry += `  delegate: {\n`

  const rootTypes = ['Query', 'Mutation']
  for (const typeName of rootTypes) {
    const type = typeMap[typeName]
    if (type && isObjectType(type)) {
      for (const [fieldName, field] of Object.entries(type.getFields())) {
        const {tsType, isNullable} = resolveType(field.type)
        let argsType = '{}'
        const args = field.args
        if (args.length > 0) {
          const params = args
            .map(arg => {
              const {tsType: argTsType, isNullable: argIsNullable} =
                resolveType(arg.type)
              return `${arg.name}${argIsNullable ? '?' : ''}: ${argTsType}`
            })
            .join('; ')
          argsType = `{ ${params} }`
        }
        registry += `    '${typeName}.${fieldName}': {\n`
        registry += `      args: ${argsType};\n`
        registry += `      return: ${tsType}${isNullable ? ' | null' : ''};\n`
        registry += `    };\n`
      }
    }
  }
  registry += `  };\n`

  registry += `  types: {\n`
  for (const [typeName, type] of Object.entries(typeMap)) {
    if (typeName.startsWith('__')) continue
    if (['Query', 'Mutation', 'Subscription'].includes(typeName)) continue

    if (isObjectType(type) || isInterfaceType(type) || isUnionType(type)) {
      registry += `    '${typeName}': ${typeName};\n`
    }
  }
  registry += `  };\n`
  registry += `}\n`

  await fs.writeFile(outputPath, tsOutput + registry)
}
